import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Global scroll handler mounted once inside the Router. Two behaviours:
 *  - If the URL has a hash (e.g. /docs#bulk), scroll the element with that id into view.
 *    Runs after the route's content is mounted (small rAF delay) so the target exists.
 *  - If the URL has no hash and the pathname changed, scroll back to the top — fixes the
 *    "I navigated to /privacy but the page is still scrolled mid-way" UX.
 *
 * We intentionally don't restore scroll position on back/forward navigation; the simple
 * always-go-to-top behaviour is what users expect for a marketing/legal site.
 */
export function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // The target may not be in the DOM yet on first paint after a route swap.
      // requestAnimationFrame waits for the new route's mount + layout.
      const id = hash.slice(1);
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          // Fallback: hash referenced a missing element — at least don't leave the user
          // mid-page from the previous route.
          window.scrollTo({ top: 0 });
        }
      });
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [pathname, hash]);
  return null;
}
